import { useSelector } from "react-redux";

import type { StateType } from "src/redux/reducers";
import type { MatchControl } from "src/types/matchControl";

import { MatchControlDataSource } from "./dataSources";
import type { DataSourceAdapter, DataSourceType, SuggestionOption } from "./types";

/**
 * Registry for managing data source adapters
 */
export class DataSourceRegistry {
	private adapters: Map<DataSourceType, DataSourceAdapter> = new Map();

	constructor() {
		// Register default adapters
		this.register(new MatchControlDataSource());
	}

	/**
	 * Register a new data source adapter
	 */
	register(adapter: DataSourceAdapter): void {
		this.adapters.set(adapter.type, adapter);
	}

	/**
	 * Unregister a data source adapter
	 */
	unregister(type: DataSourceType): boolean {
		return this.adapters.delete(type);
	}

	/**
	 * Get a specific adapter by type
	 */
	getAdapter(type: DataSourceType): DataSourceAdapter | undefined {
		return this.adapters.get(type);
	}

	/**
	 * Get all registered adapter types
	 */
	getAvailableTypes(): DataSourceType[] {
		return Array.from(this.adapters.keys());
	}

	/**
	 * Process multiple data sources and return combined suggestions
	 * SIMPLIFIED: Handle domain shortcuts directly without complex routing
	 */
	processDataSources(
		state: unknown,
		sources: DataSourceType[],
		query?: string,
	): SuggestionOption[] {
		const allOptions: SuggestionOption[] = [];

		for (const sourceType of sources) {
			try {
				const adapter = this.adapters.get(sourceType);
				if (!adapter || !adapter.isAvailable(state)) {
					continue;
				}

				const data = adapter.extractData(state);
				if (data) {
					// SIMPLIFIED: Only handle domain shortcuts (!) directly
					if (query?.startsWith("!") && adapter.processSpecialCharacterQuery) {
						const domainOptions = adapter.processSpecialCharacterQuery(query, data);
						allOptions.push(...domainOptions);
					} else {
						const options = adapter.process(data);
						allOptions.push(...options);
					}
				}
			} catch (error) {
				// In production, avoid console spam
				if (process.env.NODE_ENV === "development") {
					console.error(`Error processing data source ${sourceType}:`, error);
				}
			}
		}

		return allOptions;
	}

	/**
	 * Check if any of the specified sources are available
	 */
	hasAvailableSources(state: unknown, sources: DataSourceType[]): boolean {
		return sources.some((sourceType) => {
			const adapter = this.adapters.get(sourceType);
			return adapter && adapter.isAvailable(state);
		});
	}
}

// Default registry instance
export const dataSourceRegistry = new DataSourceRegistry();

/**
 * Hook to get suggestions from specified data sources
 */
export const useDataSources = (sources: DataSourceType[], query?: string): SuggestionOption[] => {
	const reduxState = useSelector((state: StateType) => state);
	return dataSourceRegistry.processDataSources(reduxState, sources, query);
};

/**
 * Hook to check if data sources are available
 */
export const useDataSourcesAvailable = (sources: DataSourceType[]): boolean => {
	const reduxState = useSelector((state: StateType) => state);
	return dataSourceRegistry.hasAvailableSources(reduxState, sources);
};

/**
 * Hook to get match control data directly
 * This abstracts away the Redux state access and provides a clean interface
 */
export const useMatchControlData = (): {
	data: MatchControl | null;
	isAvailable: boolean;
	isLoading: boolean;
	error: string | null;
} => {
	const reduxState = useSelector((state: StateType) => state);
	const adapter = dataSourceRegistry.getAdapter("matchControl");

	if (!adapter) {
		return {
			data: null,
			isAvailable: false,
			isLoading: false,
			error: null,
		};
	}

	const matchControlState = reduxState.matchControls?.matchControl;

	return {
		data: adapter.extractData(reduxState) as MatchControl | null,
		isAvailable: adapter.isAvailable(reduxState),
		isLoading: matchControlState?.loading || false,
		error: matchControlState?.error || null,
	};
};
